import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { forkJoin, Observable, of } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { ChartOptions, ChartData } from 'chart.js';
import { NgChartsModule } from 'ng2-charts';
import { AuthService } from '../../shared/services';
import { RouterModule } from '@angular/router';
import { TooltipComponent } from '../../shared/tooltip/tooltip.component';
import * as bootstrap from 'bootstrap';
import { jwtDecode } from 'jwt-decode';
import { MobileMainDashboardViewComponent } from './mobile-main-dashboard-view/mobile-main-dashboard-view.component';
import { ItemType } from '../../../shared/models/item-type';

export interface DashboardSummary {
  totalRawMaterials: number;
  totalRawMaterialValue: number;
  totalProducts: number;
  totalProductValue: number;
  totalFixedOverheads: number;
  totalVariableOverheads: number;
  totalOverheadsValue: number;
  totalOrders: number;
  totalPendingOrders: number;
  totalCompletedTodayOrders: number;
  totalInProgressOrders: number;
}

export interface OverheadCostPoolDTO {
  overheadCostPoolID: number;
  poolName: string;
  finishedGoodID: number;
  description?: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface OverheadCostItemDTO {
  overheadCostItemID: number;
  overheadName: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CostBreakdown {
  category: string;
  amount: number;
  percentage?: number;
}

export interface ProductProject {
  productName: string;
  quantity: number;
  value: number;
}

export interface RawMaterial {
  materialName: string;
  quantity: number;
  uom: string;
  value: number;
}

export interface FixedOverhead {
  name: string;
  amount: number;
  paymentPeriod: string;
}

export interface VariableOverhead {
  name: string;
  totalCost: number;
  totalQuantity: number;
  uom: string;
  unitCost: number;
}

interface OverheadItem {
  name: string;
  amount: number;
  type: 'fixed' | 'variable';
}

export interface KeyCostDriver {
  name: string;
  amount: number;
}

export interface ItemDTO {
  itemID: number;
  itemCode: string;
  itemName: string;
  itemTypeID: number;
  categoryID: number;
  description?: string;
  unitOfMeasureID: number;
  quantity: number;
  unitCost: number;
  purchasePrice: number;
  purchaseQuantity: number;
  purchaseUOMID: number;
  sellingPrice: number;
  supplierID: number;
  lowStockThreshold: number;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
  profitMarginPercent?: number;
  valuationMethodCode?: string;
}

export interface InventoryTransactionDTO {
  transactionID: number;
  itemID: number;
  warehouseID: number;
  transactionType: string;
  quantityChange: number;
  costPerUnit: number;
  transactionDate: string;
  referenceType: string;
  referenceID: string;
}

export interface BOMHeaders {
  bomHeaderID: number;
  finishedGoodItemID: number;
  bomName: string;
  isActive: boolean;
}

export interface BOMComponents {
  bomComponentID: number;
  bomHeaderID: number;
  rawMaterialItemID: number;
  quantityRequired: number;
}

@Component({
  selector: 'app-main-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgChartsModule,
    RouterModule,
    TooltipComponent,
    MobileMainDashboardViewComponent,
  ],
  templateUrl: './main-dashboard.component.html',
  styleUrls: ['./main-dashboard.component.css'],
})
export class MainDashboardComponent implements OnInit {
  dashboardSummary: DashboardSummary = {
    totalRawMaterials: 0,
    totalRawMaterialValue: 0,
    totalProducts: 0,
    totalProductValue: 0,
    totalFixedOverheads: 0,
    totalVariableOverheads: 0,
    totalOverheadsValue: 0,
    totalOrders: 0,
    totalPendingOrders: 0,
    totalCompletedTodayOrders: 0,
    totalInProgressOrders: 0,
  };

  costBreakdownData: CostBreakdown[] = [];
  productProjectsData: ProductProject[] = [];
  rawMaterialsData: RawMaterial[] = [];
  fixedOverheadsData: FixedOverhead[] = [];
  variableOverheadsData: VariableOverhead[] = [];
  overheadItemsData: OverheadItem[] = [];
  keyCostDriversData: KeyCostDriver[] = [];

  // Store data for chart initialization
  private storedTransactions: InventoryTransactionDTO[] = [];
  private storedItemsWithQuantity: (ItemDTO & { quantity: number; unitCost: number })[] = [];
  
  // UOM list for lookup - will be fetched from API
  uomList: any[] = [];

  // Chart data
  costAllocationData!: ChartData<'bar'>;
  costAllocationOptions!: ChartOptions<'bar'>;
  profitTrendData: any = {}; // Using any to allow mixed chart types
  profitTrendOptions!: ChartOptions<'bar'>;

  userRole: string | null = null;
  tenantName: any;
  fullName: any;
  isLoading: boolean = true;

  // Search and filter properties
  rawMaterialsSearchTerm: string = '';
  productsSearchTerm: string = '';
  fixedOverheadsSearchTerm: string = '';
  variableOverheadsSearchTerm: string = '';
  
  // Filter selections
  selectedRawMaterialUOM: string | null = null;
  selectedFixedCostPeriod: string | null = null;
  selectedVariableCostUOM: string | null = null;
  
  // Unique filter values
  uniqueRawMaterialUOMs: string[] = [];
  uniqueFixedCostPeriods: string[] = [];
  uniqueVariableCostUOMs: string[] = [];
  
  // Filter panel visibility
  isFilterPanelOpenRawMaterials: boolean = false;
  isFilterPanelOpenProducts: boolean = false;
  isFilterPanelOpenOverheads: boolean = false;
  isFilterPanelOpenFixedOverheads: boolean = false;
  isFilterPanelOpenVariableOverheads: boolean = false;
  
  // Filtered data arrays
  filteredRawMaterialsData: RawMaterial[] = [];
  filteredProductProjectsData: ProductProject[] = [];
  filteredFixedOverheadsData: FixedOverhead[] = [];
  filteredVariableOverheadsData: VariableOverhead[] = [];

  baseColors = [
    '#007A8F',
    '#6C7A3E',
    '#B0213C',
    '#26284F',
    '#7FBF7F',
    '#F3D250',
    '#2C6B47',
    '#A0D83A',
    '#3C9C8E',
    '#FF6F61',
    '#FF2E17',
  ];

  constructor(
    private http: HttpClient,
    public authService: AuthService,
  ) {}

  ngOnInit(): void {
    const token = this.authService.getToken && this.authService.getToken();
    if (!token) {
      this.authService.logOut();
      return;
    }
    const decodedToken: any = jwtDecode(token);
    this.tenantName = decodedToken.TenantName;
    this.fullName = decodedToken.FullName;
    this.userRole = this.authService.getUserRole();

    this.loadDashboardData();
  }

  private loadDashboardData(): void {
    this.isLoading = true;
    
    forkJoin({
      inventoryData: this.getInventoryData(),
      overheads: this.getOverheadsData(),
      uoms: this.getUOMs(),
      productionOrders: this.getProductionOrders()
    }).subscribe({
      next: (result) => {
        this.uomList = result.uoms;
        this.processInventoryData(result.inventoryData);
        this.processOverheadsData(result.overheads);
        this.processProductionOrdersData(result.productionOrders);
        // Initialize filtered data
        this.initializeFilteredData();
        // Initialize charts AFTER all data sets are processed
        this.initializeChartsAfterDataLoaded();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading dashboard data:', error);
        this.isLoading = false;
        if (error.status === 401) {
          this.authService.logOut();
        }
      }
    });
  }

  private getInventoryData(): Observable<any> {
    const token = localStorage.getItem('authToken');
    if (!token) return of(null);
    
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });

    // Get all necessary data
    return forkJoin({
      items: this.http.get<any[]>(`${environment.apiUrl}/api/Items/GetAll`, { headers }),
      transactions: this.http.get<InventoryTransactionDTO[]>(`${environment.apiUrl}/api/InventoryTransactions/GetAll`, { headers }),
      bomHeaders: this.http.get<BOMHeaders[]>(`${environment.apiUrl}/api/BOMHeaders/GetAll`, { headers }),
      bomComponents: this.http.get<BOMComponents[]>(`${environment.apiUrl}/api/BOMComponents/GetAll`, { headers }),
    });
  }

  private getOverheadsData(): Observable<any> {
    const token = this.authService.getToken();
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

    return forkJoin({
      pools: this.http.get<OverheadCostPoolDTO[]>(`${environment.apiUrl}/api/OverheadCostPool/GetAll`, { headers }),
      fixedItems: this.http.get<OverheadCostItemDTO[]>(`${environment.apiUrl}/api/OverheadCostItem/GetAll`, { headers }),
      variableItems: this.http.get<ItemDTO[]>(`${environment.apiUrl}/api/Items/ByType/3`, { headers })
    });
  }

  private getUOMs(): Observable<any[]> {
    const token = this.authService.getToken();
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.get<any[]>(`${environment.apiUrl}/api/UnitOfMeasure/GetAll`, { headers });
  }

  private getProductionOrders(): Observable<any[]> {
    const token = this.authService.getToken();
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.http.get<any[]>(`${environment.apiUrl}/api/ProductionOrders/GetAll`, { headers });
  }

  private processInventoryData(data: any): void {
    if (!data) return;

    const items: ItemDTO[] = data.items;
    const transactions: InventoryTransactionDTO[] = data.transactions;
    const bomHeaders: BOMHeaders[] = data.bomHeaders;
    const bomComponents: BOMComponents[] = data.bomComponents;
    
    console.log('=== DEBUGGING PRODUCT VALUE ===');
    console.log('Items:', items);
    console.log('Transactions:', transactions);
    console.log('BOM Headers:', bomHeaders);
    console.log('BOM Components:', bomComponents);
    
    // Calculate quantity for each item based on transactions
    const itemsWithQuantity: (ItemDTO & { quantity: number; unitCost: number })[] = items.map((item: ItemDTO) => {
      const itemTransactions = transactions.filter((tx: InventoryTransactionDTO) => tx.itemID === item.itemID);
      const quantity = this.calculateItemQuantity(item.itemID, itemTransactions);
      
      // Get the latest unit cost from inventory transactions
      const latestTransaction = itemTransactions
        .filter((tx: InventoryTransactionDTO) => tx.costPerUnit !== null && tx.costPerUnit !== undefined)
        .sort((a: InventoryTransactionDTO, b: InventoryTransactionDTO) => new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime())[0];

      const unitCost = latestTransaction ? latestTransaction.costPerUnit : (item.unitCost || 0);
      
      return {
        ...item,
        quantity,
        unitCost
      };
    });

    const rawMaterials = itemsWithQuantity.filter((item: ItemDTO) => item.itemTypeID === ItemType.RawMaterial && item.isActive);
    // Spec §2: a Service is a product type too, so it counts towards Products.
    // It carries no stock, so it contributes 0 to the value total below.
    const products = itemsWithQuantity.filter(
      (item: ItemDTO) =>
        (item.itemTypeID === ItemType.FinishedGood || item.itemTypeID === ItemType.Service) && item.isActive
    );
    
    console.log('Raw Materials:', rawMaterials);
    console.log('Products:', products);
    
    this.dashboardSummary.totalRawMaterials = rawMaterials.length;
    this.dashboardSummary.totalProducts = products.length;
    
    // Calculate raw material value using unit cost from transactions
    this.dashboardSummary.totalRawMaterialValue = rawMaterials.reduce((sum: number, item: ItemDTO) => {
      return sum + (item.quantity || 0) * (item.unitCost || 0);
    }, 0);
    
    console.log('Total Raw Material Value:', this.dashboardSummary.totalRawMaterialValue);
    
    // Calculate product value using BOM
    this.dashboardSummary.totalProductValue = 0;
    
    for (const product of products) {
      console.log('\n--- Processing product:', product.itemName, 'ID:', product.itemID, 'Quantity:', product.quantity, 'Unit Cost:', product.unitCost, '---');
      
      // Get BOM for this product
      const productBOM = bomHeaders.filter((bom: BOMHeaders) => bom.finishedGoodItemID === product.itemID && bom.isActive);
      
      console.log('Product BOM found:', productBOM.length, 'BOMs');
      
      if (productBOM.length > 0) {
        // Get all BOM components for this product
        const bomComponentIds = productBOM.map((bom: BOMHeaders) => bom.bomHeaderID);
        const components = bomComponents.filter((comp: BOMComponents) => bomComponentIds.includes(comp.bomHeaderID));
        
        console.log('BOM Components for this product:', components);
        
        // Calculate total cost for this product's BOM
        const bomCost = components.reduce((bomTotal: number, comp: BOMComponents) => {
          const componentItem = itemsWithQuantity.find((item: ItemDTO) => item.itemID === comp.rawMaterialItemID);
          if (!componentItem) {
            console.log(`Component item not found for ID: ${comp.rawMaterialItemID}`);
            return bomTotal;
          }

          const componentUnitCost = componentItem.unitCost || 0;
          const componentCost = comp.quantityRequired * componentUnitCost;
          console.log(`Component: ${componentItem.itemName}, Required Qty: ${comp.quantityRequired}, Unit Cost: R${componentUnitCost}, Component Cost: R${componentCost}`);
          return bomTotal + componentCost;
        }, 0);
        
        const productValue = product.quantity * bomCost;
        console.log(`Product BOM Cost: R${bomCost}, Product Quantity: ${product.quantity}, Final Product Value: R${productValue}`);
        this.dashboardSummary.totalProductValue += productValue;
      } else {
        console.log('No active BOM found for product:', product.itemName);
      }
    }

    console.log('\n=== FINAL TOTALS ===');
    console.log('Total Product Value:', this.dashboardSummary.totalProductValue);
    console.log('========================\n');

    // Prepare cost breakdown data
    this.costBreakdownData = [
      { category: 'Raw Materials', amount: this.dashboardSummary.totalRawMaterialValue },
      { category: 'Products Value', amount: this.dashboardSummary.totalProductValue }
    ];

    // Prepare product projects data - all active products by value
    this.productProjectsData = products
      .map((product: ItemDTO) => {
        // Calculate individual product value using BOM
        let productValue = 0;
        const productBOM = bomHeaders.filter((bom: BOMHeaders) => bom.finishedGoodItemID === product.itemID && bom.isActive);
        
        if (productBOM.length > 0) {
          const bomComponentIds = productBOM.map((bom: BOMHeaders) => bom.bomHeaderID);
          const components = bomComponents.filter((comp: BOMComponents) => bomComponentIds.includes(comp.bomHeaderID));
          
          const bomCost = components.reduce((bomTotal: number, comp: BOMComponents) => {
            const componentItem = itemsWithQuantity.find((item: ItemDTO) => item.itemID === comp.rawMaterialItemID);
            if (!componentItem) return bomTotal;
            return bomTotal + (comp.quantityRequired * (componentItem.unitCost || 0));
          }, 0);
          
          productValue = product.quantity * bomCost;
        }
        
        return {
          productName: product.itemName,
          quantity: product.quantity,
          value: productValue,
        };
      })
      .sort((a: ProductProject, b: ProductProject) => b.value - a.value);
    
    console.log('Products data length:', this.productProjectsData.length);
    console.log('Products data:', this.productProjectsData);

    // Prepare raw materials data - all active raw materials by value
    this.rawMaterialsData = rawMaterials
      .map((material: ItemDTO) => ({
        materialName: material.itemName,
        quantity: material.quantity,
        uom: this.getUOMName(material.purchaseUOMID), // Use purchaseUOMID instead
        value: (material.quantity || 0) * (material.unitCost || 0)
      }))
      .sort((a: RawMaterial, b: RawMaterial) => b.value - a.value);
    
    console.log('Raw materials data length:', this.rawMaterialsData.length);
    console.log('Raw materials data:', this.rawMaterialsData);

    // Prepare key cost drivers
    this.keyCostDriversData = [
      { name: 'Labour', amount: this.dashboardSummary.totalVariableOverheads * 0.4 },
      { name: 'Material', amount: this.dashboardSummary.totalRawMaterialValue },
      { name: 'Admin Exp', amount: this.dashboardSummary.totalFixedOverheads * 0.3 },
    ];
    
    // Store data for chart initialization
    this.storedTransactions = transactions;
    this.storedItemsWithQuantity = itemsWithQuantity;
    
    // Charts will be initialized after all data is loaded
  }

  private initializeChartsAfterDataLoaded(): void {
    this.initializeCharts(this.storedTransactions, this.storedItemsWithQuantity);
  }

  // Get the name of a unit of measure by its ID
  getUOMName(uomId: number): string {
    if (!uomId) return 'units';
    const uom = this.uomList.find(u => u.uomid === uomId);
    return uom ? uom.uomCode.replace(/2$/, '²') : 'units';
  }

  private processProductionOrdersData(orders: any[]): void {
    if (!orders) return;
    
    const today = new Date().toISOString().split('T')[0];
    
    this.dashboardSummary.totalOrders = orders.length;
    this.dashboardSummary.totalPendingOrders = orders.filter(o => o.status === 'Pending').length;
    this.dashboardSummary.totalInProgressOrders = orders.filter(o => o.status === 'In Progress').length;
    this.dashboardSummary.totalCompletedTodayOrders = orders.filter(
      (o) => (o.status === 'Completed' || o.status === 'Sold' || o.status === 'Partially Sold') && 
      o.actualEndDate?.startsWith(today)
    ).length;
    
    console.log('=== MANUFACTURING STATS ===');
    console.log('Total Orders:', this.dashboardSummary.totalOrders);
    console.log('Pending Orders:', this.dashboardSummary.totalPendingOrders);
    console.log('In Progress Orders:', this.dashboardSummary.totalInProgressOrders);
    console.log('Completed Today:', this.dashboardSummary.totalCompletedTodayOrders);
    console.log('========================');
  }

  private calculateItemQuantity(itemID: number, transactions: InventoryTransactionDTO[]): number {
    const itemTransactions = transactions.filter((tx: InventoryTransactionDTO) => tx.itemID === itemID);
    const quantity = itemTransactions.reduce((sum: number, tx: InventoryTransactionDTO) => sum + tx.quantityChange, 0);
    return quantity;
  }

  private processOverheadsData(data: any): void {
    if (!data) return;

    const { pools, fixedItems, variableItems } = data;
    
    console.log('=== DEBUGGING OVERHEADS ===');
    console.log('Fixed overhead items count:', fixedItems.length);
    console.log('Variable overhead items count:', variableItems.length);
    console.log('Fixed items:', fixedItems);
    console.log('Variable items:', variableItems);
    
    // Clear previous data
    this.overheadItemsData = [];
    this.fixedOverheadsData = [];
    this.variableOverheadsData = [];
    
    // Process fixed overhead items
    fixedItems.forEach((item: any) => {
      // Get the rate from overheadAllocationRates
      let amount = 0;
      if (item.overheadAllocationRates && item.overheadAllocationRates.length > 0) {
        // Use the first rate (could add logic to find current valid rate)
        amount = item.overheadAllocationRates[0].rateValue || 0;
      }
      
      const overhead = {
        name: item.overheadName || 'Fixed Overhead',
        amount: amount,
        type: 'fixed' as const
      };
      
      this.overheadItemsData.push(overhead);
      
      // Determine payment period from periodTypeID (5 = Monthly)
      const periodTypeID = item.overheadAllocationRates?.[0]?.periodTypeID;
      let paymentPeriod = 'Monthly';
      if (periodTypeID === 1) paymentPeriod = 'Daily';
      else if (periodTypeID === 2) paymentPeriod = 'Weekly';
      else if (periodTypeID === 3) paymentPeriod = 'Fortnightly';
      else if (periodTypeID === 4) paymentPeriod = 'Monthly';
      else if (periodTypeID === 5) paymentPeriod = 'Monthly';
      
      this.fixedOverheadsData.push({
        name: overhead.name,
        amount: overhead.amount,
        paymentPeriod: paymentPeriod
      });
    });
    
    // Process variable overhead items
    variableItems.forEach((item: any) => {
      // Only process items with itemTypeID = 3 (Variable Overheads) and isActive = true
      if (item.itemTypeID !== 3 || !item.isActive) return;
      
      // Use the actual values from the API
      const totalCost = item.purchasePrice || 0;
      const totalQuantity = item.purchaseQuantity || 1;
      const unitCost = totalQuantity > 0 ? totalCost / totalQuantity : 0;
      
      const overhead = {
        name: item.itemName || 'Variable Overhead',
        amount: totalCost,
        type: 'variable' as const
      };
      
      this.overheadItemsData.push(overhead);
      
      this.variableOverheadsData.push({
        name: overhead.name,
        totalCost: totalCost,
        totalQuantity: totalQuantity,
        uom: this.getUOMName(item.purchaseUOMID),
        unitCost: unitCost
      });
    });
    
    // Calculate totals
    this.dashboardSummary.totalFixedOverheads = this.overheadItemsData
      .filter(item => item.type === 'fixed')
      .reduce((sum, item) => sum + item.amount, 0);
    
    this.dashboardSummary.totalVariableOverheads = this.overheadItemsData
      .filter(item => item.type === 'variable')
      .reduce((sum, item) => sum + item.amount, 0);
    
    this.dashboardSummary.totalOverheadsValue = 
      this.dashboardSummary.totalFixedOverheads + this.dashboardSummary.totalVariableOverheads;
    
    console.log('Total Fixed Overheads:', this.dashboardSummary.totalFixedOverheads);
    console.log('Total Variable Overheads:', this.dashboardSummary.totalVariableOverheads);
    console.log('Total Overheads Value:', this.dashboardSummary.totalOverheadsValue);
    console.log('Overhead Items Data:', this.overheadItemsData);
    console.log('========================\n');

    // Add overheads to cost breakdown
    this.costBreakdownData.push(
      { category: 'Fixed Overheads', amount: this.dashboardSummary.totalFixedOverheads },
      { category: 'Variable Overheads', amount: this.dashboardSummary.totalVariableOverheads }
    );
  }

  private initializeCharts(transactions: InventoryTransactionDTO[], itemsWithQuantity: (ItemDTO & { quantity: number; unitCost: number })[]): void {
    // Cost Breakdown Bar Chart
    this.costAllocationData = {
      labels: ['Raw Materials', 'Products', 'Fixed Overheads', 'Variable Overheads'],
      datasets: [{
        label: 'Amount (R)',
        data: [
          this.dashboardSummary.totalRawMaterialValue,
          this.dashboardSummary.totalProductValue,
          this.dashboardSummary.totalFixedOverheads,
          this.dashboardSummary.totalVariableOverheads
        ],
        backgroundColor: [
          this.baseColors[0], // Blue for raw materials
          this.baseColors[1], // Green for products
          this.baseColors[2], // Red for fixed overheads
          this.baseColors[3]  // Purple for variable overheads
        ],
        borderWidth: 0,
        borderRadius: 4,
      }]
    };

    this.costAllocationOptions = {
      indexAxis: 'y', // This makes it a horizontal bar chart
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              return `${context.label}: R${context.parsed.x.toLocaleString()}`;
            }
          }
        }
      },
      scales: {
        x: {
          beginAtZero: true,
          ticks: {
            callback: (value: any) => {
              const numValue = Number(value);
              if (numValue >= 1000000) {
                return 'R' + (numValue / 1000000).toFixed(1) + 'M';
              } else if (numValue >= 1000) {
                return 'R' + (numValue / 1000).toFixed(0) + 'k';
              }
              return 'R' + numValue.toLocaleString();
            }
          }
        }
      }
    };

    // Monthly Cost Trend - calculate from actual transaction data
    const monthlyData = this.calculateMonthlyCostsFromTransactions(transactions, itemsWithQuantity);
    
    this.profitTrendData = {
      labels: monthlyData.labels,
      datasets: [
        {
          label: 'Raw Material Costs',
          data: monthlyData.rawMaterials,
          backgroundColor: this.hexToRgba(this.baseColors[0], 0.5), // Add transparency
          borderColor: this.baseColors[0],
          borderWidth: 2,
        },
        {
          label: 'Overhead Costs',
          data: monthlyData.overheads,
          backgroundColor: this.hexToRgba(this.baseColors[2], 0.5), // Add transparency
          borderColor: this.baseColors[2],
          borderWidth: 2,
        },
        {
          label: 'Total Costs',
          data: monthlyData.total,
          type: 'line' as any,
          borderColor: this.baseColors[1],
          backgroundColor: this.hexToRgba(this.baseColors[1], 0.2),
          borderWidth: 3,
          tension: 0.4,
          fill: true,
        }
      ]
    };

    this.profitTrendOptions = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        legend: {
          position: 'top',
        },
        tooltip: {
          enabled: true,
          mode: 'index',
          intersect: false,
          callbacks: {
            label: (context) => {
              return `${context.dataset.label}: R${context.parsed.y.toLocaleString()}`;
            }
          }
        }
      },
      scales: {
        y: {
          type: 'linear', // Back to linear scale
          beginAtZero: true,
          stacked: false,
          ticks: {
            callback: (value: any) => {
              // Smart formatting for linear scale
              if (value >= 1000000) {
                return 'R' + (Number(value) / 1000000).toFixed(1) + 'M';
              } else if (value >= 1000) {
                return 'R' + (Number(value) / 1000).toFixed(0) + 'k';
              } else {
                return 'R' + Number(value).toFixed(0);
              }
            }
          }
        },
        x: {
          stacked: false
        }
      },
      onHover: (event: any, activeElements: any[]) => {
        event.native.target.style.cursor = activeElements.length > 0 ? 'pointer' : 'default';
      }
    };
  }

  private calculateMonthlyCostsFromTransactions(transactions: InventoryTransactionDTO[], itemsWithQuantity: (ItemDTO & { quantity: number; unitCost: number })[]) {
    // Get the last 6 months of data
    const now = new Date();
    const months: string[] = [];
    const rawMaterialCosts: number[] = [];
    const overheadCosts: number[] = [];
    
    // Generate last 6 months
    for (let i = 5; i >= 0; i--) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = monthDate.toLocaleDateString('en-US', { month: 'short' });
      months.push(monthName);
      
      // Calculate costs for this month
      const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
      const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
      
      // Filter transactions for this month
      const monthTransactions = transactions.filter(tx => {
        const txDate = new Date(tx.transactionDate);
        return txDate >= monthStart && txDate <= monthEnd;
      });
      
      // Calculate raw material costs (only for raw material items that are active)
      const rawMaterialCost = monthTransactions
        .filter(tx => {
          const item = itemsWithQuantity.find(item => item.itemID === tx.itemID);
          return item && item.itemTypeID === 1 && item.isActive && tx.transactionType === 'Receipt';
        })
        .reduce((sum, tx) => sum + (tx.quantityChange * (tx.costPerUnit || 0)), 0);
      
      rawMaterialCosts.push(rawMaterialCost);
      
      // For overheads, they are the same every month
      const monthlyOverhead = this.dashboardSummary.totalFixedOverheads + this.dashboardSummary.totalVariableOverheads;
      overheadCosts.push(monthlyOverhead);
      
      console.log(`Month ${monthName}: Raw Materials = R${rawMaterialCost}, Monthly Overhead = R${monthlyOverhead}`);
    }
    
    const totalCosts = rawMaterialCosts.map((rm, i) => rm + overheadCosts[i]);
    
    return {
      labels: months,
      rawMaterials: rawMaterialCosts,
      overheads: overheadCosts,
      total: totalCosts
    };
  }

  private generateMonthlyCostData() {
    // Generate realistic monthly data based on current values
    const baseRawMaterialCost = this.dashboardSummary.totalRawMaterialValue || 45000;
    const baseOverheadCost = (this.dashboardSummary.totalFixedOverheads + this.dashboardSummary.totalVariableOverheads) || 15000;
    
    const months = ['Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const rawMaterials = months.map((_, i) => {
      // Add some variation (+/- 20%)
      const variation = 0.8 + (Math.random() * 0.4);
      return Math.round(baseRawMaterialCost * variation);
    });
    
    const overheads = months.map((_, i) => {
      // Overheads are more stable (+/- 10%)
      const variation = 0.9 + (Math.random() * 0.2);
      return Math.round(baseOverheadCost * variation);
    });
    
    const total = rawMaterials.map((rm, i) => rm + overheads[i]);
    
    return {
      labels: months,
      rawMaterials,
      overheads,
      total
    };
  }

  private hexToRgba(hex: string, alpha: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  LogOut(): void {
    this.authService.logOut();
  }

  // Search and filter methods
  filterRawMaterials(): void {
    const searchTerm = this.rawMaterialsSearchTerm.toLowerCase().trim();
    
    this.filteredRawMaterialsData = this.rawMaterialsData.filter(material => {
      const matchesSearch = !searchTerm || 
        material.materialName.toLowerCase().includes(searchTerm) ||
        material.uom.toLowerCase().includes(searchTerm);
      
      const matchesUOM = !this.selectedRawMaterialUOM || 
        material.uom === this.selectedRawMaterialUOM;
      
      return matchesSearch && matchesUOM;
    });
  }

  filterProducts(): void {
    const searchTerm = this.productsSearchTerm.toLowerCase().trim();
    
    if (!searchTerm) {
      this.filteredProductProjectsData = [...this.productProjectsData];
      return;
    }
    
    this.filteredProductProjectsData = this.productProjectsData.filter(product =>
      product.productName.toLowerCase().includes(searchTerm)
    );
  }

  filterFixedOverheads(): void {
    const searchTerm = this.fixedOverheadsSearchTerm.toLowerCase().trim();
    
    this.filteredFixedOverheadsData = this.fixedOverheadsData.filter(overhead => {
      const matchesSearch = !searchTerm || 
        overhead.name.toLowerCase().includes(searchTerm) ||
        overhead.paymentPeriod.toLowerCase().includes(searchTerm);
      
      const matchesPeriod = !this.selectedFixedCostPeriod || 
        overhead.paymentPeriod === this.selectedFixedCostPeriod;
      
      return matchesSearch && matchesPeriod;
    });
  }

  filterVariableOverheads(): void {
    const searchTerm = this.variableOverheadsSearchTerm.toLowerCase().trim();
    
    this.filteredVariableOverheadsData = this.variableOverheadsData.filter(overhead => {
      const matchesSearch = !searchTerm || 
        overhead.name.toLowerCase().includes(searchTerm) ||
        overhead.uom.toLowerCase().includes(searchTerm);
      
      const matchesUOM = !this.selectedVariableCostUOM || 
        overhead.uom === this.selectedVariableCostUOM;
      
      return matchesSearch && matchesUOM;
    });
  }

  // Toggle filter panels
  toggleFilterPanelRawMaterials(): void {
    this.isFilterPanelOpenRawMaterials = !this.isFilterPanelOpenRawMaterials;
    this.adjustCardHeight('rawMaterials', this.isFilterPanelOpenRawMaterials);
  }

  toggleFilterPanelProducts(): void {
    this.isFilterPanelOpenProducts = !this.isFilterPanelOpenProducts;
    this.adjustCardHeight('products', this.isFilterPanelOpenProducts);
  }

  toggleFilterPanelOverheads(): void {
    this.isFilterPanelOpenOverheads = !this.isFilterPanelOpenOverheads;
    this.adjustCardHeight('overheads', this.isFilterPanelOpenOverheads);
  }

  toggleFilterPanelFixedOverheads(): void {
    this.isFilterPanelOpenFixedOverheads = !this.isFilterPanelOpenFixedOverheads;
    this.adjustCardHeight('fixedOverheads', this.isFilterPanelOpenFixedOverheads);
  }

  toggleFilterPanelVariableOverheads(): void {
    this.isFilterPanelOpenVariableOverheads = !this.isFilterPanelOpenVariableOverheads;
    this.adjustCardHeight('variableOverheads', this.isFilterPanelOpenVariableOverheads);
  }

  // Store original card heights
  private originalCardHeights: { [key: string]: string } = {};

  // Adjust card height when filter is open
  private adjustCardHeight(section: string, isOpen: boolean): void {
    setTimeout(() => {
      // Only target cards in the tables section (row with mt-2 class)
      const cards = document.querySelectorAll('.row.mt-2 .metric-card') as NodeListOf<HTMLElement>;
      cards.forEach((card, index) => {
        if ((section === 'rawMaterials' && index === 0) ||
            (section === 'products' && index === 1) ||
            (section === 'fixedOverheads' && index === 2) ||
            (section === 'variableOverheads' && index === 2)) {
          
          const cardKey = `table-card-${index}`;
          
          if (isOpen) {
            // Store original height if not already stored
            if (!this.originalCardHeights[cardKey]) {
              this.originalCardHeights[cardKey] = card.style.height || '350px';
            }
            
            // Calculate the natural height
            const currentHeight = card.scrollHeight;
            card.style.height = currentHeight + 'px';
            card.style.minHeight = currentHeight + 'px';
          } else {
            // Use stored original height for smooth transition
            const originalHeight = this.originalCardHeights[cardKey] || '350px';
            card.style.height = originalHeight;
            card.style.minHeight = 'auto';
            
            // Clear the stored height after transition
            setTimeout(() => {
              card.style.minHeight = 'auto';
            }, 300);
          }
        }
      });
    }, 0);
  }

  // Initialize filtered data when data is loaded
  private initializeFilteredData(): void {
    this.filteredRawMaterialsData = [...this.rawMaterialsData];
    this.filteredProductProjectsData = [...this.productProjectsData];
    this.filteredFixedOverheadsData = [...this.fixedOverheadsData];
    this.filteredVariableOverheadsData = [...this.variableOverheadsData];
    
    // Populate unique filter values
    this.uniqueRawMaterialUOMs = [...new Set(this.rawMaterialsData.map(m => m.uom))].sort();
    this.uniqueFixedCostPeriods = [...new Set(this.fixedOverheadsData.map(o => o.paymentPeriod))].sort();
    this.uniqueVariableCostUOMs = [...new Set(this.variableOverheadsData.map(o => o.uom))].sort();
  }
}
